import React from 'react'
import { alpha, Box, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { StyledButtonTile } from './NavigationTile'
import WorkIcon from '@mui/icons-material/Work'
import BeachAccessIcon from '@mui/icons-material/BeachAccess'
import { useSelector } from 'react-redux'
import { getSidebarState } from '../../store/redux/reducers/sidebarSlice'
import { useNavigate } from 'react-router-dom'
import { getCurrentUser } from '../../store/redux/reducers/currentUserSlice'
import { monthPickerActions } from '../../store/redux/reducers/monthPickerSlice'
import { useAppDispatch } from '../../store/redux/hooks'
import FunctionsIcon from '@mui/icons-material/Functions'
import dayjs from 'dayjs'
import DateFormat from '../../store/types/enums/DateFormat'
import { User } from '../../store/types'
import { roundHours } from '../../store/utils/worklogUtils'

type MonthTileProps = {
  title: string
  workedHours: number
  vacationHours: number
  index: number
  year: number
  selectedUser: User | null
}

const MonthTile = ({ title, workedHours, vacationHours, index, year, selectedUser }: MonthTileProps) => {
  const theme = useTheme()
  const sidebarOpen = useSelector(getSidebarState)

  const sidebarWidth = sidebarOpen ? 250 : 75
  const adjustedBreakpoint = `calc(${theme.breakpoints.values.md}px - ${sidebarWidth}px)`
  const isSmallScreen = useMediaQuery(`(max-width:${adjustedBreakpoint})`)

  const navigate = useNavigate()
  const currentUser = useSelector(getCurrentUser)

  const dispatch = useAppDispatch()

  const navigateToWorkHours = () => {
    dispatch(monthPickerActions.setDate(dayjs(`${year}-${index + 1}-1`).format(DateFormat.YearMonthDay)))
    navigate(`/worklog/${selectedUser?.id || currentUser?.id}`)
  }

  return (
    <Tooltip
      title={`${title}: ${workedHours}h worked, ${vacationHours}h vacation`}
      placement="top"
      PopperProps={{
        sx: {
          opacity: isSmallScreen ? '100%' : '0%',
        },
      }}
    >
      <StyledButtonTile
        sx={{
          width: '100%',
          paddingX: isSmallScreen ? 1 : theme.spacing(3),
          minWidth: 20,
          borderColor: theme => alpha(theme.palette.primary.main, 0.5),
          overflowX: 'hidden',
          justifyContent: 'center',
          ':hover': {
            bgcolor: `background.elevation2`,
          },
          ':focus': {
            bgcolor: `background.elevation2`,
          },
          bgcolor: 'background.elevation1',
          svg: {
            width: 20,
            height: 20,
          },
        }}
        onClick={() => {
          navigateToWorkHours()
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            navigateToWorkHours()
          }
        }}
        tabIndex={0}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isSmallScreen ? 'row' : 'column',
            alignItems: isSmallScreen ? 'center' : 'flex-start',
            justifyContent: isSmallScreen ? 'space-between' : 'flex-start',

            flex: 1,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography
            variant="h3"
            noWrap
            sx={{
              color: 'text.highEmphasis',
              marginRight: 1,
              textAlign: isSmallScreen ? 'center' : 'left',
              fontSize: '1.25rem',
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WorkIcon sx={{ color: 'text.mediumEmphasis' }} />
            <Typography variant="body2" sx={{ color: 'text.mediumEmphasis', mx: 0.5 }}>
              {roundHours(workedHours)}h
            </Typography>
            <BeachAccessIcon sx={{ color: 'text.mediumEmphasis' }} />
            <Typography variant="body2" sx={{ color: 'text.mediumEmphasis', mx: 0.5 }}>
              {roundHours(vacationHours)}h
            </Typography>
            <FunctionsIcon sx={{ color: 'text.mediumEmphasis' }} />
            <Typography variant="body2" sx={{ color: 'text.mediumEmphasis', mx: 0.5 }}>
              {roundHours(workedHours + vacationHours)}h
            </Typography>
          </Box>
        </Box>
      </StyledButtonTile>
    </Tooltip>
  )
}

export default MonthTile
